import { Chip, type ChipTone } from "@nt/ui/chip";
import { Field, FieldError, FieldHint, FieldLabel } from "@nt/ui/field";
import { buttonVariants, inputVariants } from "@nt/ui/recipes";
import { useState, type FormEvent } from "react";

import { QueryView } from "@/components/query-view";
import { Section } from "@/components/section";
import {
  Questionnaire,
  Signature,
  W8benFields,
  W9Fields,
  type Values,
} from "@/components/tax-form-fields";
import { messageOf } from "@/lib/api";
import { shortDate } from "@/lib/format";
import { useGet, useWrite } from "@/lib/queries";
import { FORM_LABEL, formForAnswers, type TaxAnswers } from "@/lib/taxform";

export interface TaxStatus {
  state: "none" | "pending" | "approved" | "rejected" | "expired";
  /** "w9", "w8ben" or "w8bene". */
  formType?: string | null;
  formLabel: string | null;
  signedAt: string | null;
  expiresAt: string | null;
  reason: string | null;
  needed: boolean;
  suggestedForm: string | null;
  /** An approved form still pays, whatever the latest form is. An API that does not send it reads as false. */
  approvedFormInForce?: boolean;
}

const STATE_CHIP: Record<
  TaxStatus["state"],
  { label: string; tone: ChipTone; meaning: string }
> = {
  none: {
    label: "Not sent",
    tone: "surface",
    meaning: "We do not have a tax form from you yet.",
  },
  pending: {
    label: "Under review",
    tone: "info",
    meaning:
      "We received your form and are checking it. We email you when it is approved, or if anything is missing.",
  },
  approved: {
    label: "Approved",
    tone: "success",
    meaning: "Your form is on file. You do not need to do anything.",
  },
  rejected: {
    label: "Needs a new form",
    tone: "danger",
    meaning: "We could not accept your last form. Send a corrected one below.",
  },
  expired: {
    label: "Expired",
    tone: "warning",
    meaning:
      "Your form is no longer valid. Send a new one below so we can keep paying you.",
  },
};

/** The line under the card's title. `needed` wins: it means act now. */
const STATE_DESCRIPTION: Record<TaxStatus["state"], string> = {
  none: "We need a signed tax form on file before your first payout.",
  pending: "We have your form and are reviewing it.",
  approved: "Your signed tax form is on file.",
  rejected: "Send a new signed form so we can pay you.",
  expired: "Send a new signed form so we can keep paying you.",
};

/** The latest form was rejected, but an older approved one still pays: nothing is owed. */
const IN_FORCE_CHIP = {
  label: "Approved form in force",
  tone: "success" as ChipTone,
  meaning:
    "We could not accept your last form, but your approved form stays in force. A new form is optional.",
};

export function StatusCard({ status }: { status: TaxStatus }) {
  const inForce = status.state === "rejected" && status.approvedFormInForce;
  const chip = inForce ? IN_FORCE_CHIP : STATE_CHIP[status.state];
  // These describe the latest form. While an older one is in force, they would read as if the rejected one were.
  const latest = inForce ? null : status;
  const description = inForce
    ? "Your approved form stays in force, so we can keep paying you."
    : status.needed
      ? "Your fees have passed the payout minimum. Send a signed form now so we can pay you once they clear the hold."
      : STATE_DESCRIPTION[status.state];
  return (
    <Section title="Tax form status" description={description}>
      <div className="grid gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={chip.tone}>{chip.label}</Chip>
          {latest?.formLabel ? (
            <span className="font-medium text-foreground">
              {latest.formLabel}
            </span>
          ) : null}
        </div>
        <p className="text-foreground">{chip.meaning}</p>
        {latest?.signedAt || latest?.expiresAt ? (
          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-[auto_1fr]">
            {latest.signedAt ? (
              <>
                <dt className="text-muted-foreground">Signed</dt>
                <dd className="text-foreground">
                  {shortDate(latest.signedAt)}
                </dd>
              </>
            ) : null}
            {latest.expiresAt ? (
              <>
                <dt className="text-muted-foreground">Valid until</dt>
                <dd className="text-foreground">
                  {shortDate(latest.expiresAt)}
                </dd>
              </>
            ) : null}
          </dl>
        ) : null}
        {latest?.expiresAt && latest.formType?.startsWith("w8") ? (
          <p className="text-compact text-muted-foreground">
            A W-8 form is good through December 31 of the third year after you
            sign it.
          </p>
        ) : null}
        {status.state === "rejected" && status.reason ? (
          <p
            role="alert"
            className={
              inForce
                ? "rounded-card bg-warning-tint p-3 text-warning"
                : "rounded-card bg-danger-tint p-3 text-danger"
            }
          >
            Why: {status.reason}{" "}
            {inForce
              ? "A new form is optional."
              : "Send a corrected form below."}
          </p>
        ) : null}
      </div>
    </Section>
  );
}

/** The request body for a generated form, from the typed values. Pure, so it is tested. */
export function buildFormBody(
  kind: "w9" | "w8ben",
  v: Values,
): Record<string, unknown> {
  const opt = (s: string | undefined) => (s && s.trim() ? s.trim() : undefined);
  if (kind === "w9") {
    return {
      name: v.name,
      ...(opt(v.businessName) ? { businessName: v.businessName } : {}),
      taxClassification: v.taxClassification || "individual",
      ...(v.taxClassification === "llc"
        ? { llcClassification: v.llcClassification }
        : {}),
      address: v.address,
      cityStateZip: v.cityStateZip,
      tin: { kind: v.tinKind === "ein" ? "ein" : "ssn", value: v.tin },
    };
  }
  return {
    name: v.name,
    countryOfCitizenship: (v.citizenship ?? "").toUpperCase(),
    permanentAddress: {
      street: v.street,
      city: v.city,
      ...(opt(v.state) ? { state: v.state } : {}),
      ...(opt(v.postalCode) ? { postalCode: v.postalCode } : {}),
      country: (v.country ?? "").toUpperCase(),
    },
    ...(v.noForeignTin === "1"
      ? { foreignTinNotRequired: true }
      : { foreignTin: v.foreignTin }),
    ...(opt(v.usTin) ? { usTin: v.usTin } : {}),
    dateOfBirth: v.dateOfBirth,
  };
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new Error("We could not read that file. Choose the PDF again."));
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.readAsDataURL(file);
  });
}

const FORM_DESCRIPTION: Record<ReturnType<typeof formForAnswers>, string> = {
  w9: "Your answers point to Form W-9. Nonresident Tax is the requester named on the form.",
  w8ben:
    "Your answers point to Form W-8BEN, for individuals who are not US persons.",
  w8bene:
    "Your answers point to Form W-8BEN-E, for companies outside the US. Download the blank form, complete and sign it, then upload the PDF.",
};

/** Today's date where the partner is, as a date input wants it. */
function localToday(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** What was typed into one form, kept while the partner goes back to the questions. */
export interface TaxFormDraft {
  values: Values;
  file: File | null;
}

export function FormBody({
  answers,
  draft,
  onChangeAnswers,
}: {
  answers: TaxAnswers;
  draft?: TaxFormDraft;
  onChangeAnswers: (draft: TaxFormDraft) => void;
}) {
  const kind = formForAnswers(answers);
  const generated = useWrite<unknown, unknown>("/api/partner/tax-form", [
    "tax-form",
    "overview",
  ]);
  const upload = useWrite<unknown, unknown>("/api/partner/tax-form/upload", [
    "tax-form",
    "overview",
  ]);
  const [v, setV] = useState<Values>(draft?.values ?? {});
  const [file, setFile] = useState<File | null>(draft?.file ?? null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const pending = generated.isPending || upload.isPending;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const signature = { name: v.sigName ?? "", attest: v.attest === "1" };
      if (kind === "w8bene") {
        if (!file) throw new Error("Choose the signed W-8BEN-E PDF first.");
        if (!v.signedAt)
          throw new Error("Enter the date you signed the form, then send it.");
        await upload.mutateAsync({
          fileName: file.name,
          bytesBase64: await readBase64(file),
          answers,
          signature: { ...signature, signedAt: v.signedAt },
        });
      } else {
        await generated.mutateAsync({
          answers: { ...answers, form: buildFormBody(kind, v) },
          signature,
        });
      }
      setDone(true);
    } catch (err) {
      setError(messageOf(err));
    }
  }

  if (done)
    return (
      <Section title="Form received">
        <p role="status" className="text-sm text-success">
          Thank you. We received your form and will review it. We email you if
          anything is missing.
        </p>
      </Section>
    );
  return (
    <form className="flex flex-col gap-6" onSubmit={(e) => void submit(e)}>
      <Section
        title={FORM_LABEL[kind]}
        description={FORM_DESCRIPTION[kind]}
        actions={
          <button
            type="button"
            className={buttonVariants({ variant: "ghost", size: "sm" })}
            onClick={() => onChangeAnswers({ values: v, file })}
          >
            Change answers
          </button>
        }
      >
        {kind === "w9" ? <W9Fields v={v} setV={setV} /> : null}
        {kind === "w8ben" ? <W8benFields v={v} setV={setV} /> : null}
        {kind === "w8bene" ? (
          <div className="grid gap-4">
            <a
              className={buttonVariants({
                variant: "secondary",
                className: "w-fit",
              })}
              href="https://www.irs.gov/pub/irs-pdf/fw8bene.pdf"
              target="_blank"
              rel="noreferrer"
            >
              Download the blank Form W-8BEN-E
            </a>
            <Field>
              <FieldLabel htmlFor="file">Signed PDF (up to 10 MB)</FieldLabel>
              <input
                id="file"
                type="file"
                accept="application/pdf"
                required={!file}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className={inputVariants()}
              />
              <FieldHint>
                {file ? `Chosen: ${file.name}` : "PDF only."}
              </FieldHint>
            </Field>
            <Field>
              <FieldLabel htmlFor="signedAt">
                Date you signed the form
              </FieldLabel>
              <input
                id="signedAt"
                type="date"
                required
                max={localToday()}
                value={v.signedAt ?? ""}
                onChange={(e) => setV({ ...v, signedAt: e.target.value })}
                className={inputVariants({ className: "max-w-48" })}
              />
              <FieldHint>
                The date next to your signature on the PDF. The form is good
                through December 31 of the third year after it.
              </FieldHint>
            </Field>
          </div>
        ) : null}
      </Section>
      <Section
        title="Sign and send"
        description="Your typed name is your signature on the form."
      >
        <div className="grid gap-4">
          <Signature values={v} set={setV} />
          {error ? <FieldError role="alert">{error}</FieldError> : null}
          <div>
            <button
              type="submit"
              disabled={pending || v.attest !== "1"}
              className={buttonVariants({ size: "lg" })}
            >
              {pending ? "Sending…" : "Sign and send"}
            </button>
          </div>
        </div>
      </Section>
    </form>
  );
}

/**
 * `readOnly` is a deactivated partner, who is still paid what they earned (spec 7.4): the API takes this form from
 * them, so the page says why it is open. A banned partner never gets here: `App` shows the closed notice instead.
 */
export function TaxFormView({
  status,
  readOnly,
}: {
  status: TaxStatus;
  readOnly: boolean;
}) {
  const [replacing, setReplacing] = useState(false);
  const [answers, setAnswers] = useState<TaxAnswers | null>(null);
  const [lastAnswers, setLastAnswers] = useState<TaxAnswers | null>(null);
  const [drafts, setDrafts] = useState<
    Partial<Record<ReturnType<typeof formForAnswers>, TaxFormDraft>>
  >({});
  const canSend =
    status.state === "none" ||
    status.state === "rejected" ||
    status.state === "expired" ||
    (status.state === "approved" && replacing);
  return (
    <>
      {readOnly ? (
        <p className="text-sm text-muted-foreground">
          Your account is read-only, but you can still send a tax form so we can
          pay what you earned.
        </p>
      ) : null}
      <StatusCard status={status} />
      {status.state === "approved" && !replacing ? (
        <Section
          title="Need to change something?"
          description="Send a new form if your details changed. Your approved form stays in force until we approve the new one."
        >
          <button
            type="button"
            className={buttonVariants({ variant: "secondary" })}
            onClick={() => setReplacing(true)}
          >
            Replace this form
          </button>
        </Section>
      ) : null}
      {canSend ? (
        answers ? (
          <FormBody
            answers={answers}
            draft={drafts[formForAnswers(answers)]}
            onChangeAnswers={(draft) => {
              setDrafts({ ...drafts, [formForAnswers(answers)]: draft });
              setLastAnswers(answers);
              setAnswers(null);
            }}
          />
        ) : (
          <Questionnaire onDone={setAnswers} initial={lastAnswers} />
        )
      ) : null}
    </>
  );
}

/** The Settings › Tax form tab. */
export function TaxFormTab({ readOnly }: { readOnly: boolean }) {
  const query = useGet<TaxStatus>("tax-form", "/api/partner/tax-form");
  return (
    <QueryView query={query} what="your tax form status">
      {(status) => <TaxFormView status={status} readOnly={readOnly} />}
    </QueryView>
  );
}
